<style scoped>
.register-form {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
}
.register-form ::v-deep .form-row {
    @apply w-full;
}

@screen lg {
    .register-form ::v-deep .form-row {
        @apply w-1/2 px-2 my-4;
    }

    .register-form ::v-deep .form-row > .form-label {
        @apply font-bold;
    }
}
</style>

<template>
    <div class="max-w-site mx-auto">
        <pageLoader v-if="!pageLoaded" />
        <div v-else class="mt-4 px-4 py-6 border border-gray-100">
            <div class="w-full text-center my-4 text-xl font-bold">
                {{ $t('register.greet') }}
            </div>
            <div class="py-8 px-4 sm:p-8 register-form">
                <formFieldRowByRule
                    :name="['surname', 'givenName']"
                    :label="$t('register.name')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <div class="flex flex-col sm:flex-row items-start">
                            <input
                                class="form-input w-full mb-2 sm:w-32 sm:mr-2 sm:mb-0"
                                :class="{ 'border-red': hasError }"
                                type="text"
                                v-model.trim="form.surname"
                                :placeholder="$t('account.surname')"
                            />
                            <input
                                class="form-input w-full sm:flex-1"
                                :class="{ 'border-red': hasError }"
                                type="text"
                                v-model.trim="form.givenName"
                                :placeholder="$t('account.given.name')"
                            />
                        </div>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="email"
                    :label="$t('account.email')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <div class="flex flex-col sm:flex-col-reverse">
                            <div v-if="!isReferral" class="text-blue text-sm">
                                {{ $t('account.email.confirm.after.register') }}
                            </div>

                            <input
                                v-if="!!isReferral"
                                class="form-input"
                                type="email"
                                :class="{ 'border-red': hasError }"
                                v-model.trim="form.email"
                            />
                            <input
                                v-else
                                class="form-input"
                                type="email"
                                :class="{ 'border-red': hasError }"
                                :readonly="!isReferral"
                                :value="form.email"
                            />
                        </div>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="confirmEmail"
                    :label="$t('account.confirm.email')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <input
                            class="form-input w-full"
                            :class="{ 'border-red': hasError }"
                            type="email"
                            v-model.trim="form.confirmEmail"
                        />
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="title"
                    :label="$t('account.title')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{ hasError }">
                        <select
                            class="form-select"
                            v-model.trim="form.title"
                            :class="{ 'border-red': hasError }"
                        >
                            <option disabled :value="null"> </option>
                            <option
                                v-for="c in genderCodeList"
                                :key="c"
                                :value="c"
                            >
                                {{ $t('account.title.' + c) }}
                            </option>
                        </select>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="birthday"
                    :label="$t('account.birthday')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template>
                        <monthPicker
                            :value="form.birthday"
                            @input="
                                v => {
                                    form.birthday = v
                                        ? v.valueFormatted
                                        : undefined;
                                }
                            "
                            :minYear="new Date().getFullYear() - 100"
                            :maxYear="new Date().getFullYear() - 18"
                            :defaultYear="new Date().getFullYear() - 50"
                            :format="'yyyy-MM-dd'"
                        />
                        <div class="text-blue">
                            {{ $t('account.birthday.remind.message') }}
                        </div>
                        <div
                            v-if="showBirthdayTcoinGiftMessage"
                            class="text-blue"
                        >
                            {{ $t('account.birthday.gift.message') }}
                        </div>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="occupation"
                    :label="$t('account.occupation')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <select
                            class="form-select"
                            v-model.trim="form.occupation"
                            :class="{ 'border-red': hasError }"
                        >
                            <option disabled :value="null"></option>
                            <option
                                v-for="j in occupationList"
                                :key="j.id"
                                :value="j.id"
                            >
                                {{ $t(j.slctDesp) }}
                            </option>
                        </select>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="jobLevel"
                    :label="$t('account.job.level')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <select
                            class="form-select"
                            v-model.trim="form.jobLevel"
                            :class="{ 'border-red': hasError }"
                        >
                            <option disabled :value="null"></option>
                            <option
                                v-for="j in jobLevelList"
                                :key="j.id"
                                :value="j.id"
                            >
                                {{ $t(j.slctDesp) }}
                            </option>
                        </select>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="monthlyIncome"
                    :label="$t('account.monthly.income')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <select
                            class="form-select"
                            v-model.trim="form.monthlyIncome"
                            :class="{ 'border-red': hasError }"
                        >
                            <option disabled :value="null"></option>
                            <option
                                v-for="j in monthlyIncomeList"
                                :key="j.id"
                                :value="j.id"
                            >
                                {{ $t(j.slctDesp) }}
                            </option>
                        </select>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="phone"
                    :label="$t('account.phone')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{ hasError }">
                        <input
                            class="form-input w-full"
                            :class="{ 'border-red': hasError }"
                            type="tel"
                            v-model.trim="form.phone"
                        />
                        <div v-if="isFriendAccount" class="text-blue text-sm">
                            {{ $t('account.phone.remind.message') }}
                        </div>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="deliverySite"
                    :label="$t('account.delivery.site')"
                    :errors="errors"
                    labelWidthClass="sm:w-24"
                    class="field-deliverySite"
                    :rules="validationRules"
                >
                    <template #default="{ hasError }">
                        <select
                            class="form-select"
                            :class="{ 'border-red': hasError }"
                            v-model.trim="form.deliverySite"
                        >
                            <option disabled :value="null"> </option>
                            <option
                                v-for="s in companySiteList"
                                :key="s.id"
                                :value="s.id"
                            >
                                {{ s.cmpySiteName }}
                            </option>
                        </select>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="departmentName"
                    :label="$t('account.department')"
                    :errors="errors"
                    labelWidthClass="sm:w-24"
                    class="field-departmentName"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <div class="flex flex-col sm:flex-col-reverse">
                            <div class="text-blue text-sm">
                                {{ $t('account.department.remind.message') }}
                            </div>

                            <input
                                class="form-input"
                                :class="{ 'border-red': hasError }"
                                v-model.trim="form.departmentName"
                            />
                        </div>
                    </template>
                </formFieldRowByRule>
                <div class="w-full"></div>
                <formFieldRowByRule
                    name="password"
                    :label="$t('login.password')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <div class="flex flex-col sm:flex-col-reverse">
                            <div class="text-sm text-darkGrayishBlue">
                                {{ $t('login.password.hint') }}
                            </div>
                            <input
                                class="form-input"
                                type="password"
                                :class="{ 'border-red': hasError }"
                                v-model.trim="form.password"
                            />
                        </div>
                    </template>
                </formFieldRowByRule>
                <formFieldRowByRule
                    name="confirmPassword"
                    :label="$t('account.confirm.password')"
                    labelWidthClass="sm:w-24"
                    :errors="errors"
                    :rules="validationRules"
                >
                    <template #default="{hasError}">
                        <input
                            class="form-input"
                            type="password"
                            :class="{ 'border-red': hasError }"
                            v-model.trim="form.confirmPassword"
                        />
                    </template>
                </formFieldRowByRule>
                <div class="w-full sm:ml-24 sm:pl-2">
                    <div class="mt-2 mb-4">
                        <agreeTermsCheckbox v-model="form.agreeTerms" />
                        <div
                            v-if="errors.agreeTerms"
                            class="text-red text-sm ml-10"
                        >
                            {{ $t(errors.agreeTerms) }}
                        </div>
                    </div>
                    <div>
                        <checkbox v-model="form.agreePromotions">
                            {{ $t('account.agree.promotion') }}
                        </checkbox>
                    </div>
                </div>
            </div>
            <errorBox :message="errors.server" />
            <div class="flex text-lg">
                <button
                    class="btn w-1/2 mr-2 justify-center"
                    :disabled="saveStatus !== undefined"
                    @click="clearForm"
                >
                    {{ $t('common.reset') }}
                </button>
                <loadingButton
                    class="btn bg-primary w-1/2 ml-2 justify-center font-black"
                    :status="saveStatus"
                    @click="confirm"
                    @successShown="$router.push({ name: 'home' })"
                >
                    {{ $t('common.confirm') }}
                </loadingButton>
            </div>
        </div>
    </div>
</template>

<script>
import errorBox from '@/components/errorBox.vue';
import checkbox from '@/components/checkbox.vue';
import formFieldRowByRule from '@/components/formFieldRowByRule.vue';
import loadingButton from '@/components/loadingButton.vue';
import pageLoader from '@/components/pageLoader.vue';
import { instance } from '@/utils/ajax';
import { validate, isValidPassword } from '@/utils/validationUtil';
import { wait } from '@/utils/promiseUtil';
import monthPicker from '../../components/monthPicker.vue';
import agreeTermsCheckbox from '../../components/agreeTermsCheckbox.vue';

async function getRegisterInfo(token) {
    try {
        let result = await instance.get('/api/register/getRegisterInfo', {
            params: { token }
        });
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error getting register info');
    }
    return undefined;
}

async function getReferralInfo(token, refCustKey) {
    try {
        let result = await instance.get('/api/register/getReferralInfo', {
            params: { token, refCustKey }
        });
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error getting referral info');
    }
    return undefined;
}

async function register(form) {
    try {
        let result = await instance.post('/api/register/register', form);
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error register');
        return { success: false, all: 'error.connect' };
    }
}

async function referralRegister(form) {
    try {
        let result = await instance.post(
            '/api/register/referralRegister',
            form
        );
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error register');
        return { success: false, all: 'error.connect' };
    }
}

const validationRulesGeneral = {
    surname: { required: true, show: true },
    givenName: { required: true, show: true },
    title: { required: true, show: true },
    jobLevel: { required: true, show: true },
    monthlyIncome: { required: true, show: true },
    birthday: { required: true, show: true },
    deliverySite: { required: true, show: true },
    departmentName: { required: !!false, show: true },
    email: { required: true, show: true },
    confirmEmail: {
        required: !!false,
        show: !!false,
        emailSame: function(val, d) {
            if (val && val !== d.email) {
                return 'error.confirm.email.not.equal';
            }
        }
    },
    password: {
        required: true,
        show: true,
        passwordPattern: function(v) {
            if (!isValidPassword(v)) {
                return 'error.password.format';
            }
        }
    },
    confirmPassword: {
        required: true,
        show: true,
        passwordSame: function(val, d) {
            if (val !== d.password) {
                return 'error.confirm.password.not.equal';
            }
        }
    },
    agreeTerms: {
        checked: function(val) {
            if (val !== true) {
                return 'register.error.agree.terms';
            }
        }
    },
    phone: { required: true, show: true },
    occupation: { required: !!false, show: !!false }
};

const validationRulesReferral = {
    ...validationRulesGeneral,
    confirmEmail: {
        ...validationRulesGeneral.confirmEmail,
        required: true,
        show: true
    }
};

const validationRulesFriends = {
    ...validationRulesGeneral,
    deliverySite: { required: !!false, show: !!false },
    departmentName: { required: !!false, show: !!false },
    occupation: { required: !!false, show: true },
    jobLevel: { required: !!false, show: true },
    monthlyIncome: { required: !!false, show: true },
    birthday: { required: !!false, show: true }
};

const validationRulesPublic = {
    ...validationRulesGeneral,
    deliverySite: { required: !!false, show: !!false },
    departmentName: { required: !!false, show: !!false },
    occupation: { required: !!false, show: true },
    jobLevel: { required: !!false, show: true },
    monthlyIncome: { required: !!false, show: true },
    birthday: { required: !!false, show: true }
};

export default {
    components: {
        checkbox,
        formFieldRowByRule,
        loadingButton,
        pageLoader,
        errorBox,
        monthPicker,
        agreeTermsCheckbox
    },
    async created() {
        this.pageLoaded = false;

        const isReferral = this.isReferral;
        const token = isReferral
            ? this.$route.params.token
            : this.$route.params.key;
        const refCustKey = this.$route.params.refCustKey;
        this.form.token = token;
        this.form.refCustKey = refCustKey;
        const result = isReferral
            ? await getReferralInfo(token, refCustKey)
            : await getRegisterInfo(token);
        if (!result || !result.data) {
            this.$router.push({ name: 'pageNotFound' });
            return;
        }
        this.form.email = result.data.email;
        this.registerType = result.data.registerType;
        this.genderCodeList = result.data.genderCodeList;
        this.companySiteList = result.data.companySiteList;
        this.jobLevelList = result.data.jobLevelList;
        this.monthlyIncomeList = result.data.monthlyIncomeList;
        this.occupationList = result.data.occupationList;
        this.showBirthdayTcoinGiftMessage =
            result.data.showBirthdayTcoinGiftMessage;

        // Load data if customer is already created in EBP
        this.form.surname = result.data.customer?.custLastName;
        this.form.givenName = result.data.customer?.custFrstName;
        this.form.phone = result.data.customer?.custPhoneNum;
        this.form.departmentName = result.data.customer?.deptName;

        this.pageLoaded = true;
    },
    data() {
        return {
            pageLoaded: false,
            form: {
                token: undefined,
                surname: undefined,
                givenName: undefined,
                email: undefined,
                title: undefined,
                birthday: undefined,
                jobLevel: undefined,
                monthlyIncome: undefined,
                phone: undefined,
                deliverySite: undefined,
                departmentName: undefined,
                password: undefined,
                confirmPassword: undefined,
                agreeTerms: true,
                agreePromotions: true,
                // (Account Type Code = Friend) only
                occupation: undefined,
                // Referral only
                refCustKey: undefined,
                confirmEmail: undefined
            },
            errors: {},
            saveStatus: undefined,
            showTerms: false,
            showPrivacy: false,
            registerType: undefined,
            genderCodeList: [],
            companySiteList: [],
            jobLevelList: [],
            monthlyIncomeList: [],
            occupationList: [],
            showBirthdayTcoinGiftMessage: false
        };
    },
    computed: {
        isReferral() {
            return this.$route.name === 'referral';
        },
        isFriendAccount() {
            return this.registerType === 'FRND';
        },
        validationRules() {
            if (this.isReferral) {
                return validationRulesReferral;
            }
            if (this.registerType === 'FRND') {
                return validationRulesFriends;
            }
            if (this.registerType === 'PUB') {
                return validationRulesPublic;
            }
            return validationRulesGeneral;
        }
    },
    methods: {
        clearForm() {
            this.form = {
                token: this.form.token,
                surname: undefined,
                givenName: undefined,
                email: this.form.email,
                confirmEmail: undefined,
                title: undefined,
                birthday: undefined,
                jobLevel: undefined,
                monthlyIncome: undefined,
                phone: undefined,
                deliverySite: undefined,
                departmentName: undefined,
                password: undefined,
                confirmPassword: undefined,
                agreeTerms: true,
                agreePromotions: true,
                refCustKey: this.form.refCustKey
            };
            this.errors = {};
        },
        async confirm() {
            this.errors = {};
            this.form.phone = this.form.phone?.replace(/\s/g, '');
            const v = validate(this.errors, this.form, this.validationRules);
            if (v.hasError) {
                this.errors = v.errors;
                return;
            }

            this.saveStatus = 'loading';
            const result = this.isReferral
                ? await referralRegister(this.form)
                : await register(this.form);
            if (result.success) {
                let loginResult = await wait(
                    () => this.$store.dispatch('login', this.form),
                    500
                );

                if (loginResult.success) {
                    this.saveStatus = 'success';
                }
            } else {
                this.saveStatus = undefined;
                this.errors = result.data.errors;
            }
        }
    }
};
</script>
